import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { ErrorResponse } from '@core/error/error-response.model';
import { TariffGroup } from '@api/models/TariffEditorApi/Contract/tariff-group';
import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { CustomRxState } from '@core/state/rx.state';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { TariffService } from '@api/services/tariff.service';
import { TariffGroupsService } from '@api/services/tariff-groups.service';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { tap } from 'rxjs/operators';
import { AddDisplayTariffGroupRequest } from '@api/models/TariffEditorApi/Contract/add-display-tariff-group-request';
import { AddDisplayTariffGroupResponse } from '@api/models/TariffEditorApi/Contract/add-display-tariff-group-response';
import { SetDisplayTariffGroupRequest } from '@api/models/TariffEditorApi/Contract/set-display-tariff-group-request';
import {
    GetDisplayTariffGroupDataResponse,
} from '@api/models/TariffEditorApi/Contract/get-display-tariff-group-data-response';


interface ITariffGroupsState {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    tariffGroups: TariffGroup[];

}

interface TariffGroupsActions {
    requestTariffGroups: TariffGroup[];
}

@Injectable()
export class TariffGroupsState extends CustomRxState<ITariffGroupsState> {
    readonly #actions: RxActions<TariffGroupsActions> = new RxActionFactory<TariffGroupsActions>().create();

    readonly #tariffService: TariffService = inject(TariffService);
    readonly #tariffGroupsService: TariffGroupsService = inject(TariffGroupsService);

    public readonly tariffGroups$: Observable<TariffGroup[]> = this.select('tariffGroups');
    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestTariffGroups();
    }

    public requestTariffGroups(): void {
        this.hold(
            this.#tariffService.apiTariffListTariffGroupGet(),
            this.#actions.requestTariffGroups,
        );
    }

    public createTariffGroup(body: AddDisplayTariffGroupRequest): Observable<AddDisplayTariffGroupResponse> {
        return this.#tariffGroupsService.apiTariffAddDisplayTariffGroupPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditTariffGroup),
            tap(() => this.requestTariffGroups()),
        );
    }

    public editTariffGroup(body: SetDisplayTariffGroupRequest): Observable<void> {
        return this.#tariffGroupsService.apiTariffSetDisplayTariffGroupPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditTariffGroup),
            tap(() => this.requestTariffGroups()),
        );
    }

    public deleteTariffGroup(dtgId: number): Observable<void> {
        return this.#tariffGroupsService.apiTariffRemoveDisplayTariffGroupDelete({ dtgId }).pipe(
            startWithLoading(this, StartWithUnit.DeleteTariffGroup),
            tap(() => this.requestTariffGroups()),
        );
    }

    public getDetailDataTariffGroup(dtgId: number): Observable<GetDisplayTariffGroupDataResponse> {
        return this.#tariffService.apiTariffGetDisplayTariffGroupDataGet({ dtgId });
    }

    private connectSelectors(): void {
        this.connect('tariffGroups', this.#actions.requestTariffGroups$);
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            tariffGroups: null,
        });
    }
}
